import { useCallback, useState } from "react"

import {
  DefaultModality,
  RealtimeSubscribeToAttendeeIdPresenceCallback,
  VideoTileState,
} from "amazon-chime-sdk-js"

export type RosterEntry = {
  attendeeId: string
  externalUserId: string
  sharingVideo?: boolean
  sharingContent?: boolean
  videoTileId?: number
}

export type CloudroomRosterHandlers = {
  updateRosterOnAttendeePresenceEvent: RealtimeSubscribeToAttendeeIdPresenceCallback
  updateRosterOnUpdatedVideoTile: (tileState: VideoTileState) => void
  updateRosterOnRemovedVideoTile: (tileId: number) => void
  clearRoster: () => void
}

const useRoster = () => {
  const [roster, setRosterState] = useState<Record<string, RosterEntry>>({})

  const updateRosterOnAttendeePresenceEvent = useCallback(
    (presentAttendeeId: string, present: boolean, externalUserId?: string) => {
      const userType = externalUserId?.split("-")[0]
      if (!userType || ["ghost", "aws:MediaPipeline"].includes(userType)) return

      const baseAttendeeId = new DefaultModality(presentAttendeeId).base()
      if (baseAttendeeId !== presentAttendeeId) return

      if (present) {
        setRosterState(currentRoster => {
          const newRoster = { ...currentRoster }
          newRoster[presentAttendeeId] = {
            attendeeId: presentAttendeeId,
            externalUserId: externalUserId ?? "",
          }
          return newRoster
        })
      } else {
        setRosterState(currentRoster => {
          const newRoster = { ...currentRoster }
          delete newRoster[presentAttendeeId]
          return newRoster
        })
      }
    },
    []
  )

  const updateRosterOnUpdatedVideoTile = useCallback(
    (tileState: VideoTileState) => {
      if (!tileState.boundAttendeeId) return

      const baseAttendeeId = new DefaultModality(
        tileState.boundAttendeeId
      ).base()

      setRosterState(currentRoster => {
        if (!currentRoster[baseAttendeeId]) return currentRoster

        const newRoster = { ...currentRoster }

        if (tileState.isContent) {
          if (currentRoster[baseAttendeeId].sharingContent) return currentRoster

          newRoster[baseAttendeeId].videoTileId = tileState.tileId
          newRoster[baseAttendeeId].sharingVideo = false
          newRoster[baseAttendeeId].sharingContent = tileState.active
        } else {
          newRoster[baseAttendeeId].videoTileId = tileState.tileId
          newRoster[baseAttendeeId].sharingVideo = tileState.active
          newRoster[baseAttendeeId].sharingContent = false
        }

        return newRoster
      })
    },
    []
  )

  const updateRosterOnRemovedVideoTile = useCallback((tileId: number) => {
    setRosterState(currentRoster => {
      const newRoster = { ...currentRoster }
      const attendeeId = Object.keys(currentRoster).find(
        id => newRoster[id].videoTileId === tileId
      )

      if (!attendeeId || !newRoster[attendeeId]) return currentRoster

      delete newRoster[attendeeId].videoTileId
      newRoster[attendeeId].sharingVideo = false
      newRoster[attendeeId].sharingContent = false
      return newRoster
    })
  }, [])

  const clearRoster = useCallback(() => {
    setRosterState({})
  }, [])

  const rosterHandlers: CloudroomRosterHandlers = {
    updateRosterOnAttendeePresenceEvent,
    updateRosterOnUpdatedVideoTile,
    updateRosterOnRemovedVideoTile,
    clearRoster,
  }

  return { roster, rosterHandlers }
}

export default useRoster
